import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Button, capitalize, Checkbox } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import * as S from 'commons/components/Table/Table.styles';
import {
  InventoryAdjustmentSearchCriteriaEnum,
  InventoryAdjustmentStatusEnum,
} from 'commons/enums';
import {
  InventoryAdjustment,
  InventoryAdjustmentDataTableProps,
} from 'commons/types';
import { RowTitleProps } from 'commons/types/listPageData.interface';
import { TableActionRow, DataTable } from 'pages/dashboard/commons/components';
import {
  IOpenBulkDialogData,
  IOpenDialogData,
} from 'pages/dashboard/order-lists/type/open-dialog-state.interface';
import { RootReducerInterface } from 'redux-stores/reducers';
import { AppDispatch } from 'redux-stores/store';

import {
  OpenBulkDialogConfig,
  OpenDialogConfig,
} from '../configs/open-dialog.config';

import { ActionBtnMenuInventoryAdjustment } from './ActionBtnMenuInventoryAdjustment.component';

const rowTitles: RowTitleProps[] = [
  { title: 'Document Number' },
  { title: 'Status' },
  { title: 'Posting Period' },
  { title: 'Adjustment Account' },
  { title: 'Created By' },
  { title: 'Memo' },
];

const Row: React.FC<{
  data: InventoryAdjustment;
  onCheckboxClick: (skuCode: string) => void;
  checked: boolean;
  selectedLocation?: number;
  statusFilter?: InventoryAdjustmentSearchCriteriaEnum | null;
}> = ({ data, onCheckboxClick, checked, selectedLocation, statusFilter }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] = useState<IOpenDialogData | null>(
    null,
  );

  const [detailsOpen, setDetailsOpen] = useState<boolean>(false);

  const handleDetails = (): void => {
    setDetailsOpen(!detailsOpen);
  };

  const handleOpenDialog = (name: string): void => {
    setOpenDialogData(OpenDialogConfig[name.toLocaleLowerCase()]);
    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpenDialogData(null);
    setOpen(false);
  };

  return (
    <>
      <S.TableRow>
        <S.TableColumn>
          <Checkbox
            onClick={() => onCheckboxClick(data.document_number)}
            checked={checked}
          />
        </S.TableColumn>
        <S.TableColumn>{data.document_number}</S.TableColumn>
        <S.TableColumn>
          {capitalize(data.status.replace(/-/g, ' '))}
        </S.TableColumn>
        <S.TableColumn>{data.posting_period_name || '-'}</S.TableColumn>
        <S.TableColumn>{data.adjustment_account?.name || '-'}</S.TableColumn>
        <S.TableColumn>{data.created_by}</S.TableColumn>
        <S.TableColumn>{data.memo}</S.TableColumn>
        <S.TableColumn style={{ textAlign: 'right' }}>
          {data.status === InventoryAdjustmentStatusEnum.UPDATE_FAILED ? (
            <Button
              onClick={() => handleOpenDialog('retry')}
              variant="outlined"
              sx={{
                width: 87,
              }}
            >
              Retry
            </Button>
          ) : null}
        </S.TableColumn>
      </S.TableRow>
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={openDialogData?.content(data.document_number) || ''}
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            data.document_number,
            selectedLocation,
          )
        }
      />
    </>
  );
};

const InventoryAdjustmentTable: React.FC<InventoryAdjustmentDataTableProps> = (
  params,
): React.ReactNode => {
  const { data = [], statusFilter }: InventoryAdjustmentDataTableProps = params;
  const dispatch = useDispatch<AppDispatch>();

  const [checkAll, setCheckAll] = useState<boolean>(false);
  const [checkedTasks, setCheckedTasks] = useState<string[]>([]);
  const [open, setOpen] = useState<boolean>(false);
  const [openDialogData, setOpenDialogData] =
    useState<IOpenBulkDialogData | null>(null);

  const selectedTasks = data.filter((task) =>
    checkedTasks.includes(task.document_number),
  );

  const { selectedLocation } = useSelector(
    (state: RootReducerInterface) => state.dashboard,
  );

  const selectedInventoryAdjustmentNumbers = selectedTasks.map(
    (task) => task.document_number,
  );

  const handleCheckTask = (inventoryAdjustmentNumber: string): void => {
    if (checkedTasks.includes(inventoryAdjustmentNumber)) {
      // remove skuCode from checkedTasks
      checkedTasks.splice(checkedTasks.indexOf(inventoryAdjustmentNumber), 1);
      setCheckedTasks([...checkedTasks]);
    } else {
      // insert stock transfer number to checkedOrders
      setCheckedTasks([...checkedTasks, inventoryAdjustmentNumber]);
    }
  };

  const handleCheckAll = (): void => {
    if (checkAll) {
      // remove all check list
      setCheckedTasks([]);
    } else {
      // add all sku code to checkedTasks
      setCheckedTasks(data.map((task) => task.document_number));
    }
    setCheckAll(!checkAll);
  };

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleClickMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = (): void => {
    setAnchorEl(null);
  };

  const handleOpenDialog = (name: string): void => {
    setOpenDialogData(OpenBulkDialogConfig[name]);
    setOpen(true);
  };

  const handleCloseDialog = (): void => {
    setOpenDialogData(null);
    setOpen(false);
  };

  return (
    <>
      <TableActionRow
        dataName="inventory adjustment"
        selectedData={selectedTasks}
        anchorEl={anchorEl}
        openMenu={openMenu}
        handleClickMenu={handleClickMenu}
      >
        <ActionBtnMenuInventoryAdjustment
          selectedTask={selectedTasks}
          handleCloseMenu={handleCloseMenu}
          handleOpen={handleOpenDialog}
        />
      </TableActionRow>
      <DataTable
        handleCheckAll={handleCheckAll}
        checkAll={checkAll}
        rowTitles={rowTitles}
      >
        <S.TableBody>
          {data.map((item) => (
            <Row
              data={item}
              key={item.document_number}
              onCheckboxClick={handleCheckTask}
              checked={checkedTasks.includes(item.document_number)}
              selectedLocation={selectedLocation?.warehouse_id}
              statusFilter={statusFilter}
            />
          ))}
        </S.TableBody>
      </DataTable>
      {data.length > 0 && (
        <TableActionRow
          dataName="inventory adjustment"
          selectedData={selectedTasks}
          anchorEl={anchorEl}
          openMenu={openMenu}
          handleClickMenu={handleClickMenu}
        >
          <ActionBtnMenuInventoryAdjustment
            selectedTask={selectedTasks}
            handleCloseMenu={handleCloseMenu}
            handleOpen={handleOpenDialog}
          />
        </TableActionRow>
      )}
      <ConfirmationDialog
        title={openDialogData?.title || ''}
        content={
          openDialogData?.content(selectedInventoryAdjustmentNumbers) || ''
        }
        onClose={() => handleCloseDialog()}
        openModal={open}
        onConfirm={() =>
          openDialogData?.onConfirm(
            dispatch,
            selectedInventoryAdjustmentNumbers,
            selectedLocation?.warehouse_id,
          )
        }
      />
    </>
  );
};

export default InventoryAdjustmentTable;
